/**
 * The page canvas is the width the app actually gets to lay out in: the viewport minus any
 * strip the assistant drawer has reserved. Every responsive decision in the app is supposed to
 * key off THIS, not `window.innerWidth` — a drawer that takes 466px narrows the page exactly
 * as much as dragging the window in by 466px does.
 */

/** Below this the canvas is unusable; a smaller number is a measurement bug, not a layout. */
export const CANVAS_MIN_WIDTH = 320;

/**
 * The `max-width` breakpoints the app's CSS actually uses. Each becomes an `lte-<n>` token so a
 * styled-component can select on it — styled-components v5 compiles `@container` to invalid CSS
 * (stylis v3 strips the selector), so an attribute on <html> is the only readout it can consume.
 */
export const CANVAS_THRESHOLDS = [1024, 1440, 1520, 1620] as const;

export const CANVAS_ATTR = 'data-canvas';
export const CANVAS_WIDTH_VAR = '--page-canvas-width';

/**
 * Resolves the canvas width from raw measurements, or null when none of them are usable.
 *
 * Null is not a fallback width, it is a refusal: `css-mediaquery` answers false to EVERY width
 * query when the value is 0 or absent, so a bad number would report a page that is
 * simultaneously not-narrow and not-wide and collapse the whole app's layout at once.
 */
export function resolveCanvasWidth(clientWidth: number, innerWidth: number, inset: number): number | null {
  // clientWidth is what matchMedia matches against (it excludes a classic scrollbar), so it wins;
  // jsdom reports 0 for it, which is why innerWidth is a fallback rather than the other way round.
  const viewport = usableWidth(clientWidth) ?? usableWidth(innerWidth);
  if (viewport === null) return null;

  const reserved = Number.isFinite(inset) && inset > 0 ? inset : 0;
  return Math.max(CANVAS_MIN_WIDTH, Math.round(viewport - reserved));
}

function usableWidth(value: number): number | null {
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** The `data-canvas` value for a width, e.g. `"lte-1520 lte-1620"` at 1454. */
export function canvasTokens(width: number): string {
  return CANVAS_THRESHOLDS.filter((threshold) => width <= threshold)
    .map((threshold) => `lte-${threshold}`)
    .join(' ');
}
